/**
 * DPC Form Validation - Messages d'erreur inline dans la ligne du label
 * Usage: <form data-validate novalidate> + champs rendus par atom-input / atom-textarea / molecule-select
 * Règles lues depuis les attributs HTML (required, type="email") via l'API Constraint Validation
 * Déclenchement: sortie de champ, envoi, puis revalidation à la saisie tant que le champ est invalide
 * Envoi invalide: toast d'erreur générique (dépendance: toast.js), le détail reste inline
 */

const FORM_SELECTOR = "form[data-validate]";
const FIELD_SELECTOR = "[data-field]";
const CONTROL_SELECTOR = 'input:not([type="hidden"]), textarea, select';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MESSAGES = {
  valueMissing: "Champ obligatoire",
  email: "Email invalide",
  invalid: "Valeur invalide",
  submit: "Certains champs sont à corriger.",
};

const controlsOf = (field) => [...field.querySelectorAll(CONTROL_SELECTOR)];

const errorMessageOf = (control) => {
  if (control.validity.valueMissing) return MESSAGES.valueMissing;
  if (control.type === "email" && !EMAIL_REGEX.test(control.value.trim())) return MESSAGES.email;
  if (!control.validity.valid) return MESSAGES.invalid;
  return "";
};

// Retourne true si le champ est valide
const validateField = (field) => {
  const controls = controlsOf(field);
  const message = controls.map(errorMessageOf).find(Boolean) ?? "";

  controls.forEach((control) => {
    if (message) {
      control.setAttribute("aria-invalid", "true");
    } else {
      control.removeAttribute("aria-invalid");
    }
  });

  const errorSlot = field.querySelector("[data-field-error]");
  if (errorSlot) errorSlot.textContent = message;

  return message === "";
};

const fieldOf = (target) => (target instanceof Element ? target.closest(`${FORM_SELECTOR} ${FIELD_SELECTOR}`) : null);

// summary en premier : les radios de molecule-select sont dans un <details> fermé, donc non focusables
const focusField = (field) => field.querySelector(`summary, ${CONTROL_SELECTOR}`)?.focus();

// Phase de capture sur window : passe avant le listener hCaptcha injecté par Shopify,
// qui ignore preventDefault et appelle form.submit() une fois le captcha résolu
window.addEventListener(
  "submit",
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORM_SELECTOR)) return;

    const fields = [...form.querySelectorAll(FIELD_SELECTOR)];
    const invalidFields = fields
      .map((field) => ({ field, isValid: validateField(field) }))
      .filter(({ isValid }) => !isValid);

    if (invalidFields.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.DPC?.toaster?.error(MESSAGES.submit);
    focusField(invalidFields[0].field);
  },
  { capture: true }
);

document.addEventListener("focusout", (event) => {
  const field = fieldOf(event.target);
  if (!field || field.contains(event.relatedTarget)) return;
  validateField(field);
});

const revalidateIfInvalid = (event) => {
  const field = fieldOf(event.target);
  if (field?.querySelector('[aria-invalid="true"]')) validateField(field);
};

document.addEventListener("input", revalidateIfInvalid);
document.addEventListener("change", revalidateIfInvalid);
